import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  useColorModeValue,
  Button,
  Icon,
  useBreakpointValue,
  Flex,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  AspectRatio,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { FaChevronDown, FaChevronUp, FaSearchPlus } from 'react-icons/fa';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const Feature = ({ title, text, imageSrc, imagePosition, onOpen, id }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageRatio, setImageRatio] = useState(0);

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const handleImageLoad = useCallback((event) => {
    const img = event.target;
    const ratio = img.naturalWidth / img.naturalHeight;
    setImageRatio(ratio);
    setImageLoaded(true);
  }, []);

  const isNearlySquare = imageRatio > 1.3 && imageRatio < 1.9;

  const variants = {
    visible: { opacity: 1, y: 0 },
    hidden: { opacity: 0, y: 50 },
  };

  const bgGradient = useColorModeValue(
    'linear(to-r, blue.50, purple.50)',
    'linear(to-r, blue.900, purple.900)'
  );

  const textColor = useColorModeValue('gray.700', 'gray.200');
  const headingColor = useColorModeValue('blue.600', 'blue.300');

  return (
    <MotionBox
      id={id}
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={variants}
      transition={{ duration: 0.5 }}
      w="100%"
      bgGradient={bgGradient}
      position="relative"
      overflow="hidden"
      py={{ base: 16, md: 24, lg: 32 }}
    >
      <MotionFlex
        direction={{ base: 'column', xl: imagePosition === 'left' ? 'row' : 'row-reverse' }}
        align="center"
        justify="space-between"
        position="relative"
        zIndex={1}
        maxW="100%"
        px={{ base: 4, md: 6, lg: 8 }}
      >
        <VStack
          flex="1"
          align={{ base: 'center', xl: imagePosition === 'left' ? 'flex-start' : 'flex-end' }}
          spacing={8}
          textAlign={{ base: 'center', xl: imagePosition === 'left' ? 'left' : 'right' }}
          mb={{ base: 12, xl: 0 }}
          pr={{ base: 0, xl: imagePosition === 'left' ? 8 : 0 }}
          pl={{ base: 0, xl: imagePosition === 'right' ? 8 : 0 }}
          maxW={{ base: "100%", xl: "45%" }}
        >
          <Heading
            as="h3"
            fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
            fontWeight="bold"
            color={headingColor}
            textTransform="uppercase"
            letterSpacing="wide"
          >
            {title}
          </Heading>
          <Text
            fontSize={{ base: 'md', md: 'lg' }}
            color={textColor}
            lineHeight="tall"
          >
            {text}
          </Text>
        </VStack>
        <Box
          flex="1.5"
          position="relative"
          width="100%"
          maxW={{ base: "100%", xl: "50%" }}
          overflow="hidden"
          borderRadius={isNearlySquare ? "xl" : "none"}
          boxShadow={isNearlySquare ? "lg" : "none"}
          ml={{ base: 0, xl: imagePosition === 'right' ? 8 : 0 }}
          mr={{ base: 0, xl: imagePosition === 'left' ? 8 : 0 }}
        >
          <AspectRatio ratio={isNearlySquare ? 16 / 9 : imageRatio || 16 / 9}>
            <Box
              position="relative"
              cursor="pointer"
              onClick={() => onOpen(imageSrc)}
              _hover={{ '&::after': { opacity: 1 }, '> .icon': { opacity: 1 } }}
              _after={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bg: 'blackAlpha.400',
                opacity: 0,
                transition: 'opacity 0.3s',
              }}
            >
              <Image
                src={imageSrc}
                alt={title}
                objectFit={isNearlySquare ? "cover" : "contain"}
                w="100%"
                h="100%"
                onLoad={handleImageLoad}
                opacity={imageLoaded ? 1 : 0}
                transition="opacity 0.3s"
              />
              <Icon
                as={FaSearchPlus}
                boxSize={{ base: 8, md: 10 }}
                color="white"
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                opacity={0}
                className="icon"
                transition="opacity 0.3s"
                zIndex={2}
              />
            </Box>
          </AspectRatio>
        </Box>
      </MotionFlex>
    </MotionBox>
  );
};

const FeatureCard = ({ feature, scrollToFeature, isMobile }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const cardBg = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('blue.50', 'blue.900');

  return (
    <MotionBox
      borderWidth="1px"
      borderRadius="lg"
      p={4}
      bg={cardBg}
      shadow="md"
      whileHover={{ y: -5, backgroundColor: hoverBg }}
      transition={{ duration: 0.3 }}
      w="100%"
    >
      <VStack align="start" spacing={3} w="100%">
        <Heading size="md" display="flex" alignItems="center">
          <Text as="span" mr={2}>{feature.icon}</Text>
          {feature.title}
        </Heading>
        {isMobile ? (
          <>
            <Text fontSize="sm" display={isExpanded ? 'block' : 'none'}>
              {feature.text}
            </Text>
            <Button
              size="sm"
              onClick={toggleExpand}
              variant="ghost"
              rightIcon={<Icon as={isExpanded ? FaChevronUp : FaChevronDown} />}
              w="100%"
              justifyContent="space-between"
            >
              {isExpanded ? 'Weniger' : 'Mehr'}
            </Button>
          </>
        ) : (
          <Text fontSize="sm">{feature.text}</Text>
        )}
        <Button
          size="sm"
          colorScheme="blue"
          onClick={() => scrollToFeature(feature.id)}
          w="100%"
        >
          Details
        </Button>
      </VStack>
    </MotionBox>
  );
};

export default function Features() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const handleImageClick = useCallback((imageSrc) => {
    setSelectedImage(imageSrc);
    onOpen();
  }, [onOpen]);

  const features = [
    {
      title: 'Arbeitszeiterfassung',
      text: 'Präzise Erfassung von Arbeitszeiten und Pausen einfach verständlich! Optimieren Sie Ihre Produktivität durch genaue Zeiterfassung.',
      imageSrc: '/main.png',
      imagePosition: 'left',
      id: 'arbeitszeiterfassung',
      icon: '⏱️',
    },
    {
      title: 'Dashboard Statistiken',
      text: 'Umfassende Arbeitszeitstatistiken auf dem Dashboard. Analysieren Sie Gesamtarbeitszeiten, Durchschnittswerte und mehr für fundierte Entscheidungen.',
      imageSrc: '/dashboard.png',
      imagePosition: 'right',
      id: 'dashboard-statistiken',
      icon: '📊',
    },
    {
      title: 'Arbeitszeitübersicht',
      text: 'Detaillierte tabellarische Darstellung aller Arbeitszeiteinträge. Behalten Sie den Überblick und verwalten Sie Ihre Einträge effizient.',
      imageSrc: '/zeiten.png',
      imagePosition: 'left',
      id: 'arbeitszeitubersicht',
      icon: '📅',
    },
    {
      title: 'Automatische Berechnung',
      text: 'Intelligente Berechnung der Arbeitstage inklusive Feiertage. Vereinfachen Sie Ihre Planung und Zeiterfassung mit automatisierten Funktionen.',
      imageSrc: '/statistikstart.png',
      imagePosition: 'right',
      id: 'automatische-berechnung',
      icon: '🧮',
    },
    {
      title: 'Kalenderansicht',
      text: 'Intuitive Kalenderdarstellung aller Arbeitszeiten. Verschaffen Sie sich einen schnellen Überblick über Ihre Arbeitsmuster im vertrauten Kalenderformat.',
      imageSrc: '/calendar.png',
      imagePosition: 'left',
      id: 'kalenderansicht',
      icon: '📆',
    },
    {
      title: 'Benutzer- und Abteilungsverwaltung',
      text: 'Verwalten Sie als Administrator Ihre Mitarbeiter einfach und sicher mit unserer benutzerfreundlichen Oberfläche und LDAP-Synchronisation.',
      imageSrc: '/benutzer.png',
      imagePosition: 'right',
      id: 'benutzerverwaltung',
      icon: '👥',
    },
    {
      title: 'Administrationsbereich',
      text: 'Umfassender Verwaltungsbereich für Benutzer und Einstellungen. Überwachen und konfigurieren Sie das System effizient als Administrator.',
      imageSrc: '/adminbereich.png',
      imagePosition: 'left',
      id: 'administrationsbereich',
      icon: '🔧',
    },
    {
      title: 'Mehrsprachigkeit',
      text: 'Volle Unterstützung für Deutsch, Englisch und Mandarin (traditionell). Wechseln Sie flexibel zwischen den Sprachen je nach Ihren Präferenzen.',
      imageSrc: '/sprache.png',
      imagePosition: 'right',
      id: 'mehrsprachigkeit',
      icon: '🌐',
    },
    {
      title: 'Geolokalisierung',
      text: 'Automatische Zeiterfassung basierend auf Ihrem Standort. Nutzen Sie unseren Webhook für eine nahtlose und präzise standortbasierte Zeitbuchung.',
      imageSrc: '/geolocation.jpeg',
      imagePosition: 'left',
      id: 'geolokalisierung',
      icon: '📍',
    },
  ];

  const featureGroups = [
    {
      name: "Zeiterfassung",
      features: ["arbeitszeiterfassung", "arbeitszeitubersicht", "kalenderansicht"]
    },
    {
      name: "Analyse & Berechnung",
      features: ["dashboard-statistiken", "automatische-berechnung"]
    },
    {
      name: "Administration",
      features: ["benutzerverwaltung", "administrationsbereich"]
    },
    {
      name: "Zusatzfunktionen",
      features: ["mehrsprachigkeit", "geolokalisierung"]
    }
  ];

  const scrollToFeature = useCallback((id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const isMobile = useBreakpointValue({ base: true, md: false });

  const DesktopView = () => {
    const [tabIndex, setTabIndex] = useState(0);

    const handleTabsChange = (index) => {
      setTabIndex(index);
    };

    return (
      <Tabs
        isFitted
        variant="soft-rounded"
        colorScheme="blue"
        index={tabIndex}
        onChange={handleTabsChange}
      >
        <TabList mb="1em">
          {featureGroups.map((group, index) => (
            <Tab
              key={index}
              _selected={{ color: "white", bg: "blue.500" }}
              fontWeight="semibold"
            >
              {group.name}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {featureGroups.map((group, groupIndex) => (
            <TabPanel key={groupIndex}>
              <SimpleGrid columns={{ base: 1, lg: 2, xl: 3 }} spacing={6}>
                {group.features.map((featureId) => {
                  const feature = features.find(f => f.id === featureId);
                  return (
                    <FeatureCard
                      key={featureId}
                      feature={feature}
                      scrollToFeature={scrollToFeature}
                      isMobile={false}
                    />
                  );
                })}
              </SimpleGrid>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    );
  };


  const MobileView = () => (
    <Accordion allowMultiple>
      {featureGroups.map((group, groupIndex) => (
        <AccordionItem key={groupIndex}>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left" fontWeight="semibold">
                {group.name}
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            <VStack spacing={4} align="stretch">
              {group.features.map((featureId) => {
                const feature = features.find(f => f.id === featureId);
                return (
                  <FeatureCard
                    key={featureId}
                    feature={feature}
                    scrollToFeature={scrollToFeature}
                    isMobile={true}
                  />
                );
              })}
            </VStack>
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );

  return (
    <Box as="section" id="features" py={12}>
      <Container maxW="container.xl">
        <VStack spacing={8} align="stretch">
          <Heading
            as="h2"
            size="xl"
            textAlign="center"
            mb={4}
          >
            Unsere Features
          </Heading>
          {isMobile ? <MobileView /> : <DesktopView />}
        </VStack>
      </Container>

      {features.map((feature, index) => (
        <Feature key={index} {...feature} onOpen={handleImageClick} />
      ))}

      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalOverlay />
        <ModalContent bg="transparent" maxW="100vw" maxH="100vh" m="0">
          <ModalCloseButton size="lg" color="white" />
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center">
            <Image src={selectedImage} alt="Screenshot" maxW="98%" maxH="98%" objectFit="contain" />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

